import React, { useState, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import { LinkElement } from '@els/els-react--link-element';
import { Button } from '@els/els-react--button';
import InnerHTML from '@Components/InnerHTML';
import DocumentJournals from '@Components/Results/documents/DocumentJournals';
import ListOfAuthors from '@Components/Results/reactions/reaction-details/ListOfAuthors';
import { citationLiteratureFormatter } from '@Services/FormatUtils';
import externalUrl from '@Utils/ExternalUrl';
import DocumentCustomLink from '@Components/Results/documents/DocumentCustomLink';
import FullText from '@Components/Results/reactions/reaction-details/FullText';
import Catalyst from '@Services/Catalyst';
import useResultsItems from '@Containers/Summary/hooks/useResultsItems';
import { trackReferencePreview, trackSurveySubmissionForReferencePreview } from '@Containers/Summary/analytics';
import ThumbsUpDown from '@Containers/Summary/components/ThumbsUpDown';

function ReferencePreview({ index, citation, onDocumentDetailsClick, metadata }) {
  const [updatedCitedByUrl, setUpdatedCitedByUrl] = useState(externalUrl(citation.citedBy));
  const resultsItems = useResultsItems();

  useEffect(() => {
    async function updateCitedByUrl() {
      if (citation.issueCitedByCounter && citation.issueCitedByCounter > 0) {
        const _updatedCitedByUrl = await Catalyst.appendVisitorIDToUrl(externalUrl(citation.citedBy));
        setUpdatedCitedByUrl(_updatedCitedByUrl);
      }
    }

    updateCitedByUrl();
  }, [citation.issueCitedByCounter, citation.citedBy]);

  useEffect(() => {
    const { id } = citation || {};
    const resultItem = resultsItems?.find((resultsItem) => resultsItem.id === id);
    trackReferencePreview({ citationFromSummary: citation, citationFromResultsList: resultItem });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citation]);

  return (
    <section>
      <h4 className="u-els-margin-bottom-2x">Reference preview</h4>
      <p className="u-els-margin-bottom-1o2">Reference {index > 0 ? index : ''}</p>
      <InnerHTML tag="h3" html={citation?.title} className="u-els-font-size-h4 u-els-margin-bottom-1o2" />
      {!isEmpty(citation.citationJournals) && <DocumentJournals journals={citation.citationJournals} className="u-els-margin-bottom-1o2" />}
      {citation.citationPatents && (
        <InnerHTML
          tag="div"
          className="rx-element-literature u-els-margin-bottom-1o2"
          html={citationLiteratureFormatter(citation.citationPatents)}
        />
      )}
      {(citation.assignees || citation.authors) && (
        <div className="u-els-font-size-meta u-els-margin-bottom-1o2">
          {citation.assignees && (
            <>
              <span id="current-patent-assignee-label" className="u-els-display-inline-block u-els-margin-right-1o2">
                Current Patent Assignee:
              </span>
              <InnerHTML
                tag="span"
                aria-labelledby="current-patent-assignee-label"
                className="rx-element-assignee u-els-display-inline-block"
                html={citation.assignees + (citation.authors ? '; ' : '')}
              />
            </>
          )}
          {citation.authors && <ListOfAuthors metadata={metadata} citation={citation} />}
        </div>
      )}
      {citation.issueCitedByCounter > 0 && (
        <div className="u-els-margin-bottom-1o2">
          <LinkElement
            data-e2e="document-cited-count-link"
            className="e2e-cited-count"
            href={updatedCitedByUrl}
            isExternal
            noExternalIcon
            inline
            navigation>
            Cited {citation.issueCitedByCounter} times
          </LinkElement>
        </div>
      )}
      <div className="u-els-margin-top u-els-margin-bottom o-els-flex-layout o-els-flex-layout--left">
        <Button
          data-e2e="document-details-button"
          type="link"
          onClick={() => onDocumentDetailsClick(citation)}
          className="u-els-margin-right">
          Document Details
        </Button>
        {citation.fullTextCustom ? (
          <DocumentCustomLink citation={citation} getTrackingAttrs={noop} />
        ) : (
          <FullText
            citation={citation}
            trackingAttributes={{
              index,
              getTrackingAttrs: {},
            }}
            metadata={metadata}
          />
        )}
      </div>
      <h4 className="u-els-margin-bottom">Abstract</h4>
      <InnerHTML tag="p" className="u-els-margin-bottom-2x" html={citation?.abstractText} />
      <div className="o-els-flex-layout o-els-flex-layout--left">
        <span id={`summary-reference-feedback-${index}`} className="u-els-display-inline-block u-els-margin-right-1x1o2">
          Was this relevant to the question?
        </span>
        <ThumbsUpDown
          thumbsUpProps={{ 'data-e2e': 'summary-reference-like' }}
          thumbsDownProps={{ 'data-e2e': 'summary-reference-dislike' }}
          onFeedbackChange={trackSurveySubmissionForReferencePreview}
          ariaLabelledBy={`summary-reference-feedback-${index}`}
        />
      </div>
    </section>
  );
}

export default ReferencePreview;
